import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { FeatureName } from '@treams/treams-network';
import { Callout, StepTimeline, tokens } from '@treams/treams-ui';

import ConfirmReminderCallout from './confirm/ConfirmReminderCallout';

enum EvaluationSubmissionTimeLineStatus {
	PREPARING = 'PREPARING',
	SHARED = 'SHARED',
	SIGNED = 'SIGNED',
}

function getEvaluationSubmissionTimeLineStatusOrder(
	submission: graphql.EvaluationSubmissionFragment,
) {
	if (
		submission.viewer.requiresSignature ||
		submission.viewer.status === graphql.EvaluationSubmissionStatus.Signed
	) {
		return [
			EvaluationSubmissionTimeLineStatus.PREPARING,
			EvaluationSubmissionTimeLineStatus.SHARED,
			EvaluationSubmissionTimeLineStatus.SIGNED,
		];
	}

	return [EvaluationSubmissionTimeLineStatus.PREPARING, EvaluationSubmissionTimeLineStatus.SHARED];
}

function getEvaluationSubmissionTimeLineStatus(
	submission: graphql.EvaluationSubmissionFragment,
): EvaluationSubmissionTimeLineStatus {
	if (submission.viewer.viewerCanSign) {
		return EvaluationSubmissionTimeLineStatus.SHARED;
	}

	if (submission.viewer.status === graphql.EvaluationSubmissionStatus.Signed) {
		return EvaluationSubmissionTimeLineStatus.SIGNED;
	}

	if (submission.viewer.hasShared) {
		return EvaluationSubmissionTimeLineStatus.SHARED;
	}

	return EvaluationSubmissionTimeLineStatus.PREPARING;
}

function shouldWaitForEmployeeSignature(input: {
	viewerIsEmployee: boolean;
	employeeRequiresSignature: boolean;
	employeeHasSigned: boolean | null | undefined;
	onlyAllowSigningWhenEmployeeHasSigned: boolean;
}): boolean {
	if (input.viewerIsEmployee || !input.onlyAllowSigningWhenEmployeeHasSigned) {
		return false;
	}

	// Manager-only confirmation never produces an employee signature. Waiting
	// for one would block the manager after both parties have shared.
	if (!input.employeeRequiresSignature) {
		return false;
	}

	return !input.employeeHasSigned;
}

export function shouldWaitForPartnerShare(submission: {
	viewer: {
		party: graphql.EvaluationSubmissionParty;
		requiresSignature: boolean;
		status: graphql.EvaluationSubmissionStatus;
	};
	employee: {
		requiresSignature: boolean;
		requiresInput: boolean;
		status: graphql.EvaluationSubmissionStatus;
	};
	manager: {
		requiresSignature: boolean;
		requiresInput: boolean;
		status: graphql.EvaluationSubmissionStatus;
	};
}): boolean {
	const partner =
		submission.viewer.party === graphql.EvaluationSubmissionParty.Employee
			? submission.manager
			: submission.employee;

	return (
		submission.viewer.requiresSignature &&
		submission.viewer.status === graphql.EvaluationSubmissionStatus.Shared &&
		!partner.requiresSignature &&
		partner.requiresInput &&
		partner.status === graphql.EvaluationSubmissionStatus.Draft
	);
}

function showsConfirmedByPartnerCallout(input: {
	viewerCanUpdate: boolean;
	viewerCanSign: boolean;
	viewerRequiresSignature: boolean;
	viewerStatusIsSigned: boolean;
}): boolean {
	// Single-party confirmation marks the other party as signed too. That party
	// never confirmed, so the "thanks for confirming" callout does not apply.
	return (
		!input.viewerCanUpdate &&
		!input.viewerCanSign &&
		input.viewerStatusIsSigned &&
		!input.viewerRequiresSignature
	);
}

export default function EvaluationSubmissionStatus(props: {
	submission: graphql.EvaluationSubmissionFragment;
	updateLoading: boolean;
	flashSignSection: boolean;
	signChecked: boolean;
	setSignChecked: (value: boolean) => void;
	setShowSignModal: (value: boolean) => void;
}) {
	const { t } = useTranslation();

	const evaluationSubmissionTimeLineStatusOrder = getEvaluationSubmissionTimeLineStatusOrder(
		props.submission,
	);

	const viewerIsEmployee = props.submission.viewer.user.id === props.submission.employee.user.id;
	const employeeHasSigned = props.submission.employee.hasSigned;
	const onlyAllowSigningWhenEmployeeHasSigned = useFeature(
		FeatureName.OnlyAllowSigningWhenEmployeeHasSigned,
	);

	const hideConfirmationCallout = shouldWaitForEmployeeSignature({
		viewerIsEmployee,
		employeeRequiresSignature: props.submission.employee.requiresSignature,
		employeeHasSigned,
		onlyAllowSigningWhenEmployeeHasSigned,
	});

	const confirmedByPartner = showsConfirmedByPartnerCallout({
		viewerCanUpdate: props.submission.viewer.viewerCanUpdate,
		viewerCanSign: props.submission.viewer.viewerCanSign,
		viewerRequiresSignature: props.submission.viewer.requiresSignature,
		viewerStatusIsSigned:
			props.submission.viewer.status === graphql.EvaluationSubmissionStatus.Signed,
	});
	const confirmingPartnerName = viewerIsEmployee
		? props.submission.manager.user.firstName
		: props.submission.employee.user.firstName;
	const waitForPartnerShare =
		props.submission.viewer.viewerCanSign && shouldWaitForPartnerShare(props.submission);

	function getEvaluationSubmissionTimeLineStatusLabel(
		status: EvaluationSubmissionTimeLineStatus,
	): string {
		switch (status) {
			case EvaluationSubmissionTimeLineStatus.PREPARING:
				return `1. ${t('evaluations.submission.timeline.status.preparing')}`;
			case EvaluationSubmissionTimeLineStatus.SHARED:
				return `2. ${t('evaluations.submission.timeline.status.shared')}`;
			case EvaluationSubmissionTimeLineStatus.SIGNED:
				return `3. ${t('evaluations.submission.timeline.status.signed')}`;
		}
	}

	return (
		<Flex
			style={{ gap: tokens.gap.section }}
			flexDirection={['column', 'row']}
			alignItems="flex-start"
		>
			<Box flex={1}>
				<StepTimeline
					items={[
						...evaluationSubmissionTimeLineStatusOrder.map((status) => {
							return { id: status, title: getEvaluationSubmissionTimeLineStatusLabel(status) };
						}),
					]}
					activeIndex={evaluationSubmissionTimeLineStatusOrder.findIndex(
						(status) => status === getEvaluationSubmissionTimeLineStatus(props.submission),
					)}
					onItemClick={() => undefined}
					variant="non-clickable"
					showContinuousTimeLine={false}
				/>
			</Box>
			<Box flex={[2, 3]}>
				{props.submission.viewer.viewerCanShare && (
					<Callout
						mb={3}
						title={t('conversation.process.callout.share.title')}
						description={t('conversation.process.callout.share.description')}
						color={'blue'}
					/>
				)}
				{waitForPartnerShare && (
					<Callout
						mb={3}
						title={t('conversation.process.callout.partnerhasnotshared.title')}
						description={t('conversation.process.callout.partnerhasnotshared.description', {
							name: confirmingPartnerName,
						})}
						color={'yellow'}
					/>
				)}
				{props.submission.viewer.viewerCanSign &&
					props.submission.viewer.viewerCanUpdate &&
					!hideConfirmationCallout &&
					!waitForPartnerShare && (
						<ConfirmReminderCallout
							submission={props.submission as graphql.EvaluationSubmission}
							saving={props.updateLoading}
							flashSignSection={props.flashSignSection}
							signChecked={props.signChecked}
							setSignChecked={props.setSignChecked}
							setShowSignModal={props.setShowSignModal}
						/>
					)}
				{props.submission.viewer.viewerCanSign &&
					props.submission.viewer.viewerCanUpdate &&
					hideConfirmationCallout && (
						<Callout
							mb={3}
							title={t('conversation.process.callout.employeehasnotsigned.title')}
							description={t('conversation.process.callout.employeehasnotsigned.description', {
								name: props.submission.employee.user.firstName,
							})}
							color={'yellow'}
						/>
					)}
				{!props.submission.viewer.viewerCanUpdate && !props.submission.viewer.viewerCanSign && (
					<Callout
						mb={3}
						title={t('conversation.process.callout.aftersigning.title')}
						description={t(
							confirmedByPartner
								? 'conversation.process.callout.confirmedbypartner.description'
								: 'conversation.process.callout.aftersigning.description',
							{ name: confirmingPartnerName },
						)}
						color={'blue'}
					/>
				)}
				{props.submission.viewer.requiresInput &&
					!props.submission.viewer.viewerCanShare &&
					!props.submission.viewer.viewerCanSign &&
					props.submission.viewer.viewerCanUpdate && (
						<Callout
							mb={3}
							title={t('conversation.process.callout.nosignature.title')}
							description={t('conversation.process.callout.nosignature.description')}
							color={'blue'}
						/>
					)}
				{!props.submission.viewer.requiresInput &&
					!props.submission.viewer.viewerCanSign &&
					props.submission.viewer.status !== graphql.EvaluationSubmissionStatus.Signed && (
						<Callout
							mb={3}
							title={t('conversation.process.callout.noinput.title')}
							description={t('conversation.process.callout.noinput.description')}
							color={'blue'}
						/>
					)}
			</Box>
		</Flex>
	);
}
